import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { invoicesAPI, patientsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn, formatDate, formatDateTime } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Loader2,
  FileText,
  Download,
  Send,
  DollarSign,
  Receipt,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_caredata-suite/artifacts/8itk90ub_55315630-dcf1-43bd-9c0d-4f1818f5e217.jpeg";

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  
  const [patient, setPatient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeView, setActiveView] = useState(patientId ? 'create' : 'list');
  
  // New invoice form
  const [lineItems, setLineItems] = useState([
    { description: '', quantity: 1, unit_price: 0, adjustment: 0 }
  ]);
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    fetchData();
  }, [patientId]);

  const fetchData = async () => {
    try {
      if (patientId) {
        const [patientRes, invoicesRes] = await Promise.all([
          patientsAPI.getById(patientId),
          invoicesAPI.getAll({ patient_id: patientId }),
        ]);
        setPatient(patientRes.data);
        setInvoices(invoicesRes.data);
      } else {
        const invoicesRes = await invoicesAPI.getAll({});
        setInvoices(invoicesRes.data);
      }
    } catch (err) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit_price: 0, adjustment: 0 }]);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    setLineItems(updated);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalAdjustments = 0;
    
    lineItems.forEach(item => {
      subtotal += item.quantity * item.unit_price;
      totalAdjustments += item.adjustment;
    });
    
    return {
      subtotal,
      totalAdjustments,
      total: subtotal - totalAdjustments
    };
  };

  const handleCreateInvoice = async () => {
    if (!lineItems.some(item => item.description && item.unit_price > 0)) {
      toast.error('Please add at least one valid line item');
      return;
    }

    setSaving(true);
    try {
      const response = await invoicesAPI.create({
        patient_id: patientId,
        line_items: lineItems.filter(item => item.description),
        notes,
        due_date: dueDate || null,
      });
      
      toast.success('Invoice created successfully!');
      setLineItems([{ description: '', quantity: 1, unit_price: 0, adjustment: 0 }]);
      setNotes('');
      setDueDate('');
      fetchData();
      setActiveView('list');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    }
    setSaving(false);
  };

  const generatePDF = (invoice) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Header with logo
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    // Company name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Apex Medical Group', 20, 25);
    
    // Invoice title
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('INVOICE', pageWidth - 20, 20, { align: 'right' });
    doc.text(invoice.invoice_number, pageWidth - 20, 28, { align: 'right' });
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    
    // Patient details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', 20, 55);
    doc.setFont('helvetica', 'normal');
    doc.text(invoice.patient_name, 20, 62);
    if (invoice.patient_address) {
      doc.text(invoice.patient_address, 20, 69);
    }
    
    // Invoice details
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice Date:', pageWidth - 70, 55);
    doc.text('Due Date:', pageWidth - 70, 62);
    doc.text('Status:', pageWidth - 70, 69);
    
    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(invoice.created_at), pageWidth - 20, 55, { align: 'right' });
    doc.text(invoice.due_date ? formatDate(invoice.due_date) : 'Upon Receipt', pageWidth - 20, 62, { align: 'right' });
    doc.text(invoice.status.toUpperCase(), pageWidth - 20, 69, { align: 'right' });
    
    // Line items table
    const tableData = invoice.line_items.map(item => [
      item.description,
      item.quantity.toString(),
      `$${item.unit_price.toFixed(2)}`,
      item.adjustment > 0 ? `-$${item.adjustment.toFixed(2)}` : '-',
      `$${item.total.toFixed(2)}`
    ]);
    
    doc.autoTable({
      startY: 85,
      head: [['Description', 'Qty', 'Unit Price', 'Adjustment', 'Total']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [15, 23, 42] },
      styles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: 20, halign: 'center' },
        2: { cellWidth: 30, halign: 'right' },
        3: { cellWidth: 30, halign: 'right' },
        4: { cellWidth: 30, halign: 'right' },
      },
    });
    
    // Totals
    const finalY = doc.lastAutoTable.finalY + 10;
    
    doc.setFont('helvetica', 'normal');
    doc.text('Subtotal:', pageWidth - 70, finalY);
    doc.text(`$${invoice.subtotal.toFixed(2)}`, pageWidth - 20, finalY, { align: 'right' });
    
    doc.text('Adjustments:', pageWidth - 70, finalY + 7);
    doc.text(`-$${invoice.total_adjustments.toFixed(2)}`, pageWidth - 20, finalY + 7, { align: 'right' });
    
    doc.setLineWidth(0.5);
    doc.line(pageWidth - 70, finalY + 12, pageWidth - 20, finalY + 12);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Total Due:', pageWidth - 70, finalY + 20);
    doc.text(`$${invoice.total.toFixed(2)}`, pageWidth - 20, finalY + 20, { align: 'right' });
    
    // Notes
    if (invoice.notes) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', 20, finalY + 35);
      doc.setFont('helvetica', 'normal');
      doc.text(invoice.notes, 20, finalY + 42, { maxWidth: pageWidth - 40 });
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('Thank you for choosing Apex Medical Group', pageWidth / 2, 280, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
    
    // Save
    doc.save(`Invoice_${invoice.invoice_number}.pdf`);
    toast.success('Invoice PDF downloaded');
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-slate-100 text-slate-800';
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="invoices-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {patientId && (
            <Button variant="ghost" size="icon" onClick={() => navigate(`/patients/${patientId}`)} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {patientId ? 'Patient Invoices' : 'All Invoices'}
            </h1>
            {patient && (
              <p className="text-muted-foreground">
                {patient.first_name} {patient.last_name} ({patient.patient_id})
              </p>
            )}
          </div>
        </div>
        
        {patientId && hasPermission('invoices:create') && (
          <div className="flex gap-2">
            <Button
              variant={activeView === 'list' ? 'outline' : 'default'}
              onClick={() => setActiveView('create')}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              New Invoice
            </Button>
          </div>
        )}
      </div>

      {/* Create Invoice View */}
      {activeView === 'create' && patientId && (
        <Card>
          <CardHeader>
            <CardTitle>Create Invoice</CardTitle>
            <CardDescription>Add services and generate an invoice for this encounter</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Line Items */}
            <div>
              <Label className="mb-3 block">Line Items</Label>
              <div className="space-y-3">
                {lineItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-3 items-end">
                    <div className="col-span-5">
                      <Input
                        placeholder="Service description"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        data-testid={`item-description-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Qty"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                        data-testid={`item-qty-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Price"
                        min="0"
                        step="0.01"
                        value={item.unit_price || ''}
                        onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                        data-testid={`item-price-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        placeholder="Discount"
                        min="0"
                        step="0.01"
                        value={item.adjustment || ''}
                        onChange={(e) => updateLineItem(index, 'adjustment', e.target.value)}
                        data-testid={`item-adjustment-${index}`}
                      />
                    </div>
                    <div className="col-span-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" onClick={addLineItem} className="mt-3 gap-2">
                <Plus className="h-4 w-4" />
                Add Line Item
              </Button>
            </div>

            {/* Totals */}
            <div className="bg-muted p-4 rounded-lg">
              <div className="flex justify-between text-sm mb-2">
                <span>Subtotal</span>
                <span>${totals.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span>Adjustments</span>
                <span>-${totals.totalAdjustments.toFixed(2)}</span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between font-bold text-lg">
                <span>Total</span>
                <span>${totals.total.toFixed(2)}</span>
              </div>
            </div>

            {/* Additional Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  data-testid="due-date-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes or payment instructions..."
                rows={3}
                data-testid="notes-input"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button onClick={handleCreateInvoice} disabled={saving} className="gap-2" data-testid="create-invoice-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />}
                Create Invoice
              </Button>
              <Button variant="outline" onClick={() => setActiveView('list')}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoice List */}
      {(activeView === 'list' || !patientId) && (
        <Card>
          <CardHeader>
            <CardTitle>
              {invoices.length} Invoice{invoices.length !== 1 ? 's' : ''}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {invoices.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-20" />
                <p className="text-muted-foreground">No invoices found</p>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      {!patientId && <TableHead>Patient</TableHead>}
                      <TableHead>Date</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                        <TableCell>
                          <code className="text-sm bg-muted px-2 py-1 rounded">
                            {invoice.invoice_number}
                          </code>
                        </TableCell>
                        {!patientId && (
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              {invoice.patient_name}
                            </div>
                          </TableCell>
                        )}
                        <TableCell>{formatDate(invoice.created_at)}</TableCell>
                        <TableCell className="font-semibold">
                          ${invoice.total.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadge(invoice.status)}>
                            {invoice.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => generatePDF(invoice)}
                            data-testid={`download-invoice-${invoice.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
